import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { io } from "socket.io-client";
import ParticipantsList from "@/components/ParticipantsList";
import WinnerOverlay from "@/components/WinnerOverlay";
import ShareRoom from "@/components/ShareRoom";
import HostScoreboard from "@/components/HostScoreboard";
import FinalScoreboard from "@/components/FinalScoreboard";
import PlayerStats from "@/components/PlayerStats";
import RoomSettings from "@/components/RoomSettings";
import Lobby from "@/components/Lobby";
import RoundTimer from "@/components/RoundTimer";
import { playBuzzerSound, playWinnerSound, playResetSound } from "@/utils/sounds";
import { useWakeLock } from "@/hooks/useWakeLock";
import { useVoiceChat } from "@/hooks/useVoiceChat";
import { getTheme } from "@/utils/themes";
import { useAuth } from "@/contexts/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const GameRoom = () => {
  const { roomCode } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const socketRef = useRef(null);
  const stateEverLoadedRef = useRef(false);

  const { user } = useAuth();
  const userIsPro = Boolean(user?.is_pro);

  const [participantName] = useState(searchParams.get("name") || "");
  const [isHost] = useState(searchParams.get("host") === "true");
  const [participants, setParticipants] = useState([]);
  const [buzzerState, setBuzzerState] = useState("waiting");
  const [buzzerLocked, setBuzzerLocked] = useState(false);
  const [winner, setWinner] = useState(null);
  const [suspendedNames, setSuspendedNames] = useState([]);
  const [scores, setScores] = useState({});
  const [roundNumber, setRoundNumber] = useState(1);
  const [gameState, setGameState] = useState(null); // null until first server payload arrives
  const [themeId, setThemeId] = useState("default");
  const [timerEndAt, setTimerEndAt] = useState(null);
  const [timerDuration, setTimerDuration] = useState(null);
  const [roundTimerSeconds, setRoundTimerSeconds] = useState(null);
  const [hasCustomLogo, setHasCustomLogo] = useState(false);
  const [hasCustomBackground, setHasCustomBackground] = useState(false);
  const [customTextColor, setCustomTextColor] = useState(null);
  const [logoVersion, setLogoVersion] = useState(0);
  const [bgVersion, setBgVersion] = useState(0);
  const [showParticipants, setShowParticipants] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [connected, setConnected] = useState(false);
  const [kickedReason, setKickedReason] = useState(null);
  const [roomClosedReason, setRoomClosedReason] = useState(null);
  // Team mode state (populated from server payload)
  const [mode, setMode] = useState("solo");
  const [teams, setTeams] = useState([]);
  const [teamScores, setTeamScores] = useState({});
  // Voice chat (mics on/off) — room-level setting, host-adjustable in the lobby only
  const [voiceEnabled, setVoiceEnabled] = useState(false);
  // Names the host (or the players themselves) have explicitly muted —
  // overrides whatever the default mic-open logic below would otherwise say.
  const [mutedParticipants, setMutedParticipants] = useState([]);
  // True right after an answer-timer expires on the current winner — closes
  // their mic even though `winner` itself stays set (host may still award it).
  const [micSuspendedByTimer, setMicSuspendedByTimer] = useState(false);

  // Keep mobile screens awake while in a room
  useWakeLock(true);

  const theme = getTheme(themeId);

  // Apply any server state update payload uniformly
  const applyStatePayload = (data) => {
    if (data.participants !== undefined) setParticipants(data.participants || []);
    if (data.buzzer_state !== undefined) setBuzzerState(data.buzzer_state || "waiting");
    if (data.buzzer_locked !== undefined) setBuzzerLocked(Boolean(data.buzzer_locked));
    if (data.winner !== undefined) setWinner(data.winner || null);
    if (data.suspended_names !== undefined) setSuspendedNames(data.suspended_names || []);
    if (data.scores !== undefined) setScores(data.scores || {});
    if (data.round_number !== undefined) setRoundNumber(data.round_number || 1);
    if (data.game_state !== undefined) setGameState(data.game_state || "playing");
    if (data.theme !== undefined) setThemeId(data.theme || "default");
    if (data.timer_end_at !== undefined) {
      // Clock-skew-safe: derive local end time from server-supplied remaining ms.
      // A client whose Date.now() is behind the server would show inflated countdown
      // if we used the absolute server timestamp directly.
      if (!data.timer_end_at) {
        setTimerEndAt(null);
      } else if (typeof data.timer_remaining_ms === "number") {
        const newLocalEndMs = Date.now() + data.timer_remaining_ms;
        setTimerEndAt((prev) => {
          // Avoid resetting the running countdown on every unrelated re-broadcast
          if (prev) {
            const prevMs = new Date(prev).getTime();
            if (Math.abs(newLocalEndMs - prevMs) < 1500) return prev;
          }
          return new Date(newLocalEndMs).toISOString();
        });
      } else {
        // Fallback for older payloads without timer_remaining_ms
        setTimerEndAt(data.timer_end_at);
      }
    }
    if (data.timer_duration !== undefined) setTimerDuration(data.timer_duration || null);
    if (data.round_timer_seconds !== undefined)
      setRoundTimerSeconds(data.round_timer_seconds || null);
    if (data.has_custom_logo !== undefined) setHasCustomLogo(!!data.has_custom_logo);
    if (data.has_custom_background !== undefined)
      setHasCustomBackground(!!data.has_custom_background);
    if (data.custom_text_color !== undefined)
      setCustomTextColor(data.custom_text_color || null);
    if (data.logo_version !== undefined) setLogoVersion(data.logo_version || 0);
    if (data.bg_version !== undefined) setBgVersion(data.bg_version || 0);
    if (data.mode !== undefined) setMode(data.mode || "solo");
    if (data.teams !== undefined) setTeams(data.teams || []);
    if (data.team_scores !== undefined) setTeamScores(data.team_scores || {});
    if (data.voice_enabled !== undefined) setVoiceEnabled(Boolean(data.voice_enabled));
    if (data.muted_participants !== undefined) setMutedParticipants(data.muted_participants || []);
    // Mark that we've seen valid room state so we can ignore stale "error" events
    stateEverLoadedRef.current = true;
  };

  useEffect(() => {
    if (!participantName) {
      navigate("/");
      return;
    }

    socketRef.current = io(BACKEND_URL, {
      path: "/api/socket.io",
      transports: ["websocket", "polling"],
    });

    socketRef.current.on("connect", () => {
      setConnected(true);
      socketRef.current.emit("join_room", {
        room_code: roomCode,
        participant_name: participantName,
        is_host: isHost,
      });
    });

    socketRef.current.on("participant_joined", applyStatePayload);
    socketRef.current.on("participant_left", applyStatePayload);

    socketRef.current.on("buzzer_result", (data) => {
      applyStatePayload(data);
      setBuzzerState("buzzed");
      setWinner({
        name: data.winner_name,
        timestamp: data.timestamp,
        team_id: data.winner_team_id || null,
        team_name: data.winner_team_name || null,
      });
      setMicSuspendedByTimer(false);
      playWinnerSound();
    });

    socketRef.current.on("buzzer_suspended", (data) => {
      applyStatePayload(data);
      playResetSound();
    });

    socketRef.current.on("buzzer_reset", (data) => {
      applyStatePayload(data || {});
      setWinner(null);
      setBuzzerState("waiting");
      setSuspendedNames([]);
      playResetSound();
    });

    socketRef.current.on("point_awarded", (data) => {
      applyStatePayload(data);
      playResetSound();
    });

    socketRef.current.on("round_advanced", (data) => {
      applyStatePayload(data);
      playResetSound();
    });

    socketRef.current.on("game_reset", (data) => {
      applyStatePayload(data);
      playResetSound();
    });

    socketRef.current.on("game_ended", (data) => {
      applyStatePayload(data);
      playWinnerSound();
    });

    socketRef.current.on("player_kicked", applyStatePayload);
    socketRef.current.on("theme_changed", applyStatePayload);
    socketRef.current.on("player_lock_toggled", applyStatePayload);
    socketRef.current.on("suspensions_cleared", applyStatePayload);
    socketRef.current.on("buzzer_lock_toggled", applyStatePayload);
    socketRef.current.on("session_started", applyStatePayload);
    socketRef.current.on("score_adjusted", applyStatePayload);
    socketRef.current.on("team_score_adjusted", applyStatePayload);
    socketRef.current.on("teams_updated", applyStatePayload);
    socketRef.current.on("timer_started", applyStatePayload);
    socketRef.current.on("timer_cancelled", applyStatePayload);
    socketRef.current.on("timer_expired", (data) => {
      applyStatePayload(data);
      // Backend deliberately keeps `winner` set so the host can still decide
      // to award the point — but the mic promise is "until the timer runs out",
      // so close it here regardless.
      setMicSuspendedByTimer(true);
    });
    socketRef.current.on("round_timer_configured", applyStatePayload);
    socketRef.current.on("voice_setting_changed", applyStatePayload);
    socketRef.current.on("participant_mute_changed", applyStatePayload);
    socketRef.current.on("customization_updated", applyStatePayload);

    socketRef.current.on("room_closed", (data) => {
      setRoomClosedReason(data?.reason || "The room was closed by the host");
    });

    socketRef.current.on("kicked", (data) => {
      setKickedReason(data?.reason || "You were removed from the room");
    });

    socketRef.current.on("error", (data) => {
      // Ignore socket errors that arrive AFTER we've already seen valid room state
      // (multi-instance race, stale reconnects, transient hiccups).
      if (stateEverLoadedRef.current) {
        console.warn("[socket:error ignored — room already loaded]", data?.message);
        return;
      }
      // Give the socket a moment to re-attempt / reconnect before we declare failure.
      const msg = data?.message || "Could not join the room";
      setTimeout(() => {
        if (!stateEverLoadedRef.current) {
          setKickedReason(msg);
        }
      }, 2000);
    });

    socketRef.current.on("disconnect", () => {
      setConnected(false);
    });

    return () => {
      if (socketRef.current) socketRef.current.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomCode, participantName, navigate]);

  const emit = (event, payload = {}) => {
    if (socketRef.current) {
      socketRef.current.emit(event, { room_code: roomCode, ...payload });
    }
  };

  // Mic promise: in the lobby everyone defaults to open (free chat before the
  // game starts); once playing, only the host and the current buzzer winner
  // default open. Either way, an explicit mute (by the player themselves or
  // the host) always wins.
  const amISelfMuted = mutedParticipants.includes(participantName);
  const defaultMicOpen =
    gameState === "lobby"
      ? true
      : isHost || (Boolean(winner) && winner.name === participantName && !micSuspendedByTimer);
  const myMicShouldBeOpen = defaultMicOpen && !amISelfMuted;
  const onToggleMyMute = () =>
    emit("set_participant_muted", { participant_name: participantName, muted: !amISelfMuted });
  const { remoteStreams: voiceRemoteStreams, micError: voiceMicError } = useVoiceChat({
    enabled: voiceEnabled,
    socket: socketRef.current,
    connected,
    roomCode,
    micShouldBeOpen: myMicShouldBeOpen,
    participants,
  });

  const handleBuzzerPress = () => {
    if (
      buzzerState === "waiting" &&
      !suspendedNames.includes(participantName) &&
      !buzzerLocked
    ) {
      playBuzzerSound();
      emit("buzzer_pressed", { participant_name: participantName });
    }
  };

  const handleCloseRoom = () => {
    setShowSettings(false);
    setShowCloseConfirm(true);
  };

  const confirmCloseRoom = () => {
    emit("close_room");
    // The room_closed event will fire and navigate us — but as a safety net:
    setTimeout(() => navigate("/"), 500);
  };

  const isSuspended = suspendedNames.includes(participantName);
  const gameFinished = gameState === "finished";
  const inLobby = gameState === "lobby";
  const stateLoaded = gameState !== null;

  // ============ Kicked / Room Closed exit screens ============
  if (kickedReason || roomClosedReason) {
    const title = kickedReason ? "REMOVED" : "ROOM CLOSED";
    const message = kickedReason || roomClosedReason;
    return (
      <div className="min-h-screen w-full flex items-center justify-center bg-black text-white px-6 text-center">
        <div className="max-w-sm">
          <h1
            className="text-5xl mb-3"
            style={{ fontFamily: "Anton, sans-serif", color: theme.buzzerColor }}
            data-testid={kickedReason ? "kicked-title" : "room-closed-title"}
          >
            {title}
          </h1>
          <p className="text-[#A1A1AA] mb-6">{message}</p>
          <button
            onClick={() => navigate("/")}
            className="px-6 py-3 bg-white text-black rounded-full font-semibold"
            data-testid="exit-back-home"
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
  const customLogoUrl = hasCustomLogo
    ? `${backendUrl}/api/rooms/${roomCode}/logo?v=${logoVersion}`
    : null;
  const customBgUrl = hasCustomBackground
    ? `${backendUrl}/api/rooms/${roomCode}/background?v=${bgVersion}`
    : null;

  return (
    <div className="min-h-screen w-full relative overflow-x-hidden text-white font-secondary">
      {/* Background — custom (Pro) if set, otherwise theme-driven */}
      <div
        className="fixed inset-0 z-[-1] transition-all duration-700"
        style={{
          background: customBgUrl
            ? `url('${customBgUrl}') center/cover no-repeat`
            : theme.bgImage
            ? `url('${theme.bgImage}') center/cover`
            : theme.bgGradient,
        }}
      />
      {/* Dark scrim only when a custom background is set — keeps text readable over arbitrary images */}
      {customBgUrl && (
        <div className="fixed inset-0 z-[-1] bg-black/40 pointer-events-none" />
      )}

      {/* Header */}
      <div className="relative z-10 flex items-center justify-between px-4 sm:px-6 py-4 short:py-2 border-b border-white/10 backdrop-blur-lg bg-black/30">
        <div className="min-w-0">
          <h2
            className="text-2xl sm:text-3xl truncate"
            style={{ fontFamily: "Anton, sans-serif", color: theme.accentColor }}
          >
            ROOM: {roomCode}
          </h2>
          <p className="text-sm text-[#A1A1AA] truncate">
            {participantName}
            {isHost && (
              <span className="ml-2" style={{ color: theme.titleColor }}>
                • HOST
              </span>
            )}
          </p>
        </div>

        <div className="flex items-center gap-2">
          {isHost && (
            <>
              <button
                onClick={() => setShowSettings(true)}
                className="flex items-center justify-center p-2 sm:px-3 sm:py-2 bg-white/10 border border-white/20 rounded-xl hover:bg-white/20 transition-all"
                data-testid="open-settings-button"
                title="Room settings"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </button>

              <button
                onClick={() => setShowShare(true)}
                className="flex items-center gap-2 px-3 sm:px-4 py-2 border rounded-xl transition-all"
                style={{
                  background: `${theme.titleColor}22`,
                  borderColor: `${theme.titleColor}66`,
                  color: theme.titleColor,
                }}
                data-testid="share-room-button"
                title="Invite players"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                </svg>
                <span className="hidden sm:inline font-semibold text-sm">INVITE</span>
              </button>

              <button
                onClick={() =>
                  window.open(
                    `/room/${encodeURIComponent(roomCode)}/stage`,
                    "_blank",
                    "noopener,noreferrer"
                  )
                }
                className="flex items-center gap-2 px-3 sm:px-4 py-2 border rounded-xl transition-all"
                style={{
                  background: `${theme.accentColor}22`,
                  borderColor: `${theme.accentColor}66`,
                  color: theme.accentColor,
                }}
                data-testid="open-stage-button"
                title="Open Stage View (project on a big screen so everyone can see)"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 4v16M17 4v16M3 8h4m10 0h4M3 12h18M3 16h4m10 0h4M4 20h16a1 1 0 001-1V5a1 1 0 00-1-1H4a1 1 0 00-1 1v14a1 1 0 001 1z" />
                </svg>
                <span className="hidden sm:inline font-semibold text-sm">STAGE</span>
              </button>
            </>
          )}

          <button
            onClick={() => setShowParticipants(true)}
            className="flex items-center gap-2 px-4 py-2 bg-white/10 border border-white/20 rounded-xl hover:bg-white/20 transition-all"
            data-testid="show-participants-button"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
            <span className="font-semibold">{participants.filter((p) => p.socket_id).length}</span>
          </button>
          {voiceEnabled && (
            <button
              type="button"
              onClick={onToggleMyMute}
              disabled={Boolean(voiceMicError)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-xl border text-xs font-bold tracking-wide transition-all disabled:cursor-not-allowed ${
                myMicShouldBeOpen
                  ? "bg-[#9D4EDD]/20 border-[#9D4EDD]/50 text-[#9D4EDD] hover:bg-[#9D4EDD]/30"
                  : "bg-white/5 border-white/10 text-[#A1A1AA] hover:bg-white/10"
              }`}
              title={
                voiceMicError ||
                (amISelfMuted
                  ? "Muted — click to unmute"
                  : myMicShouldBeOpen
                  ? "Your mic is live — click to mute"
                  : "Click to mute (your mic isn't live right now anyway)")
              }
              data-testid="voice-status-badge"
            >
              {voiceMicError ? "🔇" : myMicShouldBeOpen ? "🎙️" : "🔈"}
            </button>
          )}
        </div>
      </div>

      {/* Hidden remote voice-chat audio — one <audio> per connected peer, nothing to see */}
      {voiceEnabled && (
        <div style={{ display: "none" }} aria-hidden="true">
          {Object.entries(voiceRemoteStreams).map(([sid, stream]) => (
            <audio
              key={sid}
              autoPlay
              playsInline
              ref={(el) => {
                if (el && el.srcObject !== stream) el.srcObject = stream;
              }}
            />
          ))}
        </div>
      )}

      {/* Game Area */}
      <div className="relative z-10 flex flex-col items-center justify-center min-h-[calc(100vh-80px)] px-4 py-8 short:py-3 short:min-h-0">
        {!stateLoaded ? (
          <div
            className="flex flex-col items-center justify-center"
            data-testid="room-loading"
          >
            <div
              className="w-14 h-14 rounded-full border-4 border-white/10 animate-spin"
              style={{ borderTopColor: theme.accentColor }}
            />
            <p className="mt-4 text-sm tracking-[0.3em] text-[#A1A1AA]">
              LOADING ROOM…
            </p>
          </div>
        ) : inLobby ? (
          <Lobby
            roomCode={roomCode}
            participants={participants}
            isHost={isHost}
            myName={participantName}
            theme={theme}
            roundTimerSeconds={roundTimerSeconds}
            mode={mode}
            teams={teams}
            onSetRoundTimer={(duration) => emit("set_round_timer", { duration })}
            onStartSession={() => emit("start_session")}
            onOpenShare={() => setShowShare(true)}
            onJoinTeam={(teamId, participantName) =>
              emit("join_team", { team_id: teamId, participant_name: participantName })
            }
            onCreateTeam={(name) => emit("create_team", { name })}
            onRenameTeam={(teamId, name) => emit("rename_team", { team_id: teamId, name })}
            onRemoveTeam={(teamId) => emit("remove_team", { team_id: teamId })}
            voiceEnabled={voiceEnabled}
            onSetVoiceEnabled={(v) => emit("set_voice_enabled", { voice_enabled: v })}
          />
        ) : isHost ? (
          <div className="w-full max-w-2xl mx-auto">
            <RoundTimer endAt={timerEndAt} duration={timerDuration} theme={theme} />
            <HostScoreboard
              participants={participants}
              scores={scores}
              roundNumber={roundNumber}
              winner={winner}
              suspendedNames={suspendedNames}
              buzzerState={buzzerState}
              buzzerLocked={buzzerLocked}
              roundTimerSeconds={roundTimerSeconds}
              theme={theme}
              mode={mode}
              teams={teams}
              teamScores={teamScores}
              onAwardPoint={(points = 1) => emit("award_point", { points })}
              onSuspendWinner={(deductPoints = 0) =>
                emit("suspend_winner", { deduct_points: deductPoints })
              }
              onResetBuzzer={() => emit("reset_buzzer")}
              onNextRound={() => emit("next_round")}
              onResetGame={() => emit("reset_game")}
              onEndGame={() => emit("end_game")}
              onKickPlayer={(name) => emit("kick_player", { participant_name: name })}
              onTogglePlayerLock={(name) => emit("toggle_player_lock", { participant_name: name })}
              onToggleBuzzerLock={() => emit("toggle_buzzer_lock")}
              onAdjustScore={(name, delta) => emit("adjust_score", { participant_name: name, delta })}
              onSetRoundTimer={(duration) => emit("set_round_timer", { duration })}
              onClearSuspensions={() => emit("clear_suspensions")}
              onMovePlayerToTeam={(name, teamId) =>
                emit("join_team", { team_id: teamId, participant_name: name })
              }
              onAdjustTeamScore={(teamId, delta) => emit("adjust_team_score", { team_id: teamId, delta })}
            />
          </div>
        ) : (
          // ============ NON-HOST PLAYER VIEW ============
          // In team mode, a late joiner without a team must pick one first.
          mode === "teams" &&
          !(participants.find((p) => p.name === participantName)?.team_id) ? (
            <div className="w-full max-w-md mx-auto" data-testid="midgame-team-picker">
              <p
                className="text-center tracking-[0.3em] text-sm font-semibold mb-2"
                style={{ color: theme.accentColor }}
              >
                PICK YOUR TEAM
              </p>
              <h1
                className="text-3xl sm:text-4xl text-white text-center mb-2"
                style={{ fontFamily: "Anton, sans-serif" }}
              >
                Which team are you on?
              </h1>
              <p className="text-sm text-[#A1A1AA] text-center mb-6">
                Tap a team card to join the game.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {teams.map((t) => (
                  <button
                    key={t.id}
                    onClick={() =>
                      emit("join_team", { team_id: t.id, participant_name: participantName })
                    }
                    className="text-left rounded-2xl border-2 p-4 transition-all active:scale-[0.98] hover:scale-[1.01]"
                    style={{ borderColor: t.color, background: `${t.color}18` }}
                    data-testid={`midgame-team-${t.id}`}
                  >
                    <div
                      className="text-lg font-bold"
                      style={{ color: t.color, fontFamily: "Anton, sans-serif" }}
                    >
                      {t.name}
                    </div>
                    <div className="text-xs text-white/60 mt-1">
                      {participants.filter((p) => p.team_id === t.id).length} member(s)
                    </div>
                  </button>
                ))}
              </div>
              {teams.length === 0 && (
                <p className="text-center text-[#FF0055] mt-4 text-sm">
                  No teams available. Ask the host to add one.
                </p>
              )}
            </div>
          ) : (
          <div className="w-full flex flex-col items-center short:flex-row short:justify-center short:items-center short:gap-8 short:max-w-5xl short:mx-auto">
            <div className="flex flex-col items-center short:items-start short:flex-1 short:max-w-sm">
              <RoundTimer endAt={timerEndAt} duration={timerDuration} theme={theme} />
              {/* Player stats panel — live score + top 3 */}
              <PlayerStats
                scores={scores}
                participants={participants}
                myName={participantName}
                theme={theme}
              />

              {buzzerState === "waiting" && !isSuspended && !buzzerLocked && (
                <div className="text-center short:text-left mb-8 short:mb-0">
                  <p
                    className="tracking-[0.3em] text-sm font-semibold mb-2 short:mb-0"
                    style={{ color: theme.accentColor }}
                  >
                    ROUND {roundNumber}
                  </p>
                  <h1
                    className="text-4xl sm:text-5xl lg:text-6xl short:text-3xl text-white mb-2 short:mb-1"
                    style={{ fontFamily: "Anton, sans-serif" }}
                  >
                    READY
                  </h1>
                  <p className="text-lg sm:text-xl short:text-sm text-[#A1A1AA]">Fastest thumb wins!</p>
                  {suspendedNames.length > 0 && (
                    <p className="text-sm short:text-xs mt-2 short:mt-1" style={{ color: theme.buzzerColor }} data-testid="suspended-count">
                      {suspendedNames.length} player{suspendedNames.length > 1 ? "s" : ""} locked out this round
                    </p>
                  )}
                </div>
              )}

              {buzzerState === "waiting" && buzzerLocked && !isSuspended && (
                <div className="text-center short:text-left mb-8 short:mb-0">
                  <h1
                    className="text-4xl sm:text-5xl lg:text-6xl short:text-3xl mb-2 short:mb-1"
                    style={{ fontFamily: "Anton, sans-serif", color: theme.buzzerColor }}
                    data-testid="buzzers-locked-title"
                  >
                    BUZZERS LOCKED
                  </h1>
                  <p className="text-lg sm:text-xl short:text-sm text-[#A1A1AA]">Wait for the host to unlock</p>
                </div>
              )}

              {buzzerState === "waiting" && isSuspended && (
                <div className="text-center short:text-left mb-8 short:mb-0">
                  <h1
                    className="text-4xl sm:text-5xl lg:text-6xl short:text-3xl mb-2 short:mb-1"
                    style={{ fontFamily: "Anton, sans-serif", color: theme.buzzerColor }}
                    data-testid="locked-out-title"
                  >
                    LOCKED OUT
                  </h1>
                  <p className="text-lg sm:text-xl short:text-sm text-[#A1A1AA]">Wait for the host to reset</p>
                </div>
              )}
            </div>

            <button
              onClick={handleBuzzerPress}
              disabled={buzzerState === "buzzed" || isSuspended || buzzerLocked}
              className="relative z-10 w-64 h-64 sm:w-80 sm:h-80 short:w-40 short:h-40 short-xs:w-32 short-xs:h-32 rounded-full flex items-center justify-center transition-transform active:scale-95 cursor-pointer outline-none select-none disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100 overflow-hidden shrink-0"
              style={{
                background: isSuspended || buzzerLocked ? "#444" : theme.buzzerColor,
                boxShadow:
                  isSuspended || buzzerLocked
                    ? "inset 0 -10px 20px rgba(0,0,0,0.5)"
                    : `0 0 60px ${theme.buzzerGlow}, inset 0 -10px 20px rgba(0,0,0,0.5), inset 0 10px 20px rgba(255,255,255,0.4)`,
                touchAction: "none",
              }}
              data-testid="buzzer-button"
            >
              {/* Optional custom logo — fills the entire round button, faded so text stays readable (Pro) */}
              {customLogoUrl && (
                <img
                  src={customLogoUrl}
                  alt=""
                  aria-hidden="true"
                  draggable="false"
                  className="pointer-events-none absolute inset-0 w-full h-full object-cover opacity-55"
                  data-testid="buzzer-custom-logo"
                />
              )}
              <span
                className="relative z-10 text-5xl sm:text-7xl short:text-3xl short-xs:text-2xl drop-shadow-[0_4px_8px_rgba(0,0,0,0.8)]"
                style={{
                  fontFamily: "Anton, sans-serif",
                  color: customTextColor || "#FFFFFF",
                }}
              >
                {buzzerLocked
                  ? "LOCKED"
                  : isSuspended
                  ? "LOCKED"
                  : buzzerState === "waiting"
                  ? "TAP"
                  : "LOCKED"}
              </span>
            </button>
          </div>
          )
        )}

        {!connected && (
          <div className="mt-8 text-[#FF0055] text-center">
            <p>Connecting to server...</p>
          </div>
        )}
      </div>

      {/* Winner overlay for non-host players */}
      {winner && !isHost && !gameFinished && (
        <WinnerOverlay
          winnerName={winner.name}
          winnerTeamName={winner.team_name || null}
          isMe={winner.name === participantName}
          timerEndAt={timerEndAt}
          timerDuration={timerDuration}
          theme={theme}
          onReset={() => {}}
          onSuspend={() => {}}
          isHost={false}
        />
      )}

      {/* Final scoreboard overlay */}
      {gameFinished && (
        <FinalScoreboard
          scores={scores}
          participants={participants}
          roundNumber={roundNumber}
          roomCode={roomCode}
          isHost={isHost}
          onResetGame={() => emit("reset_game")}
          onCloseRoom={handleCloseRoom}
          mode={mode}
          teams={teams}
          teamScores={teamScores}
        />
      )}

      {/* Room settings (host only) */}
      <RoomSettings
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
        currentTheme={themeId}
        isPro={userIsPro}
        roomCode={roomCode}
        hasCustomLogo={hasCustomLogo}
        hasCustomBackground={hasCustomBackground}
        customTextColor={customTextColor}
        logoVersion={logoVersion}
        bgVersion={bgVersion}
        onChangeTheme={(id) => emit("change_theme", { theme: id })}
        onCloseRoom={handleCloseRoom}
      />

      {/* Close room confirmation */}
      {showCloseConfirm && (
        <div
          className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-center justify-center px-4"
          onClick={() => setShowCloseConfirm(false)}
        >
          <div
            className="bg-[#1a1a1a] border border-white/10 rounded-2xl p-6 max-w-sm w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <h3
              className="text-white text-2xl mb-2"
              style={{ fontFamily: "Anton, sans-serif" }}
            >
              CLOSE THIS ROOM?
            </h3>
            <p className="text-[#A1A1AA] text-sm mb-5">
              All players will be disconnected and you'll be sent to the home page.
              You can create a new room from there.
            </p>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setShowCloseConfirm(false)}
                className="px-4 py-2 text-[#A1A1AA] hover:text-white transition-colors"
                data-testid="close-room-cancel"
              >
                Cancel
              </button>
              <button
                onClick={confirmCloseRoom}
                className="px-4 py-2 bg-[#FF0055] text-white rounded-full font-semibold hover:bg-[#FF3377] transition-colors"
                data-testid="close-room-confirm"
              >
                Close Room
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Participants drawer */}
      <ParticipantsList
        participants={participants}
        isOpen={showParticipants}
        onClose={() => setShowParticipants(false)}
        isHost={isHost}
        myName={participantName}
        voiceEnabled={voiceEnabled}
        mutedParticipants={mutedParticipants}
        onToggleMute={(name, muted) => emit("set_participant_muted", { participant_name: name, muted })}
      />

      {/* Share room modal */}
      <ShareRoom roomCode={roomCode} isOpen={showShare} onClose={() => setShowShare(false)} />
    </div>
  );
};

export default GameRoom;
